import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import SummaryApi from '../common'
import { setUserDetails } from '../store/userSlice'

// Authentication + (optional) role gate for route elements.
//
// Usage:
//   <ProtectedRoute>{children}</ProtectedRoute>                    // any logged-in user
//   <ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN', 'SUB_ADMIN']}> // restricted by role
//     {children}
//   </ProtectedRoute>
//
// Behavior:
//   - If no user in Redux, attempts ONE session-verify call to /user-details (cookie-auth);
//     populates the user on success. Shows a lightweight "Checking session…" view while in flight.
//   - If no authenticated user, redirects to /login?redirect=<currentPath>.
//   - If `roles` is provided and the user's role is not in the list, redirects to "/" (the
//     home page) rather than /login — the user IS authenticated; they just don't have access.
//     The previous version did not enforce roles at all, so any logged-in user could navigate
//     to admin URLs (the data was still server-protected, but the UI was reachable). Closes
//     audit item P1-3.

const ProtectedRoute = ({ children, roles = null }) => {
  const user = useSelector(state => state?.user?.user)
  const dispatch = useDispatch()
  const location = useLocation()
  const [checking, setChecking] = useState(false)
  const triedRef = useRef(false)

  useEffect(() => {
    // If no user in store, try to verify session once via backend.
    if (!user?._id && !triedRef.current && !checking) {
      triedRef.current = true
      setChecking(true)
      ;(async () => {
        try {
          const res = await fetch(SummaryApi.current_user.url, {
            method: SummaryApi.current_user.method,
            credentials: 'include',
          })
          const data = await res.json()
          if (data?.success && data?.data) {
            dispatch(setUserDetails(data.data))
          }
        } catch (_) {
          // ignore — fall through to login redirect below
        } finally {
          setChecking(false)
        }
      })()
    }
  }, [user?._id, checking, dispatch])

  // Authenticated branch.
  if (user?._id) {
    // Role gate, if requested.
    if (Array.isArray(roles) && roles.length > 0) {
      if (!roles.includes(user.role)) {
        // Authenticated but not authorized — send home rather than back to /login.
        return <Navigate to='/' replace />
      }
    }
    return children
  }

  if (checking) {
    return <div style={{ padding: '1rem', textAlign: 'center' }}>Checking session…</div>
  }

  // Not authenticated — remember where we wanted to go and bounce to login.
  try {
    const payload = {
      path: location.pathname + (location.search || ''),
      state: location.state || null,
      ts: Date.now(),
    }
    sessionStorage.setItem('postAuthRedirect', JSON.stringify(payload))
  } catch (_) {}

  const redirectParam = encodeURIComponent(location.pathname + (location.search || ''))
  return <Navigate to={`/login?redirect=${redirectParam}`} replace />
}

export default ProtectedRoute
